import { useState } from 'react'
import { Link } from 'react-router-dom'
import { RouteMap } from './RouteMap'
import { RouteHeader, getRouteDirection } from './RouteHeader'
import { greekToLatin, type StopInfo, type RouteInfo, type LineInfo } from '@/lib/api'
import { useGeolocation } from '@/hooks/useGeolocation'
import { LiveBuses } from './LiveBuses'

export type { StopInfo, RouteInfo, LineInfo }

type Language = 'el' | 'en'

interface StopsListProps {
  stops: StopInfo[]
  routes: RouteInfo[]
  selectedStop: StopInfo | null
  selectedLine: LineInfo | null
  selectedRoute: RouteInfo | null
  onStopSelect: (stop: StopInfo) => void
  onRouteSelect: (route: RouteInfo) => void
  lang: Language
  translations: {
    showMap: string
    showList: string
  }
}

export function StopsList({
  stops,
  routes,
  selectedStop,
  selectedLine,
  selectedRoute,
  onStopSelect,
  onRouteSelect,
  lang,
  translations: t
}: StopsListProps) {
  const [mapStopSearch, setMapStopSearch] = useState('')
  const [showMapView, setShowMapView] = useState(false)
  const { location: userLocation } = useGeolocation()

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

  const getStopDescription = (stop: StopInfo) =>
    lang === 'en' ? (stop.StopDescrEng || stop.StopDescr) : stop.StopDescr

  const getStopHref = (stop: StopInfo) => {
    const lineSlug = greekToLatin(selectedLine?.LineID || '').toLowerCase()
    return `/${lineSlug}/${selectedRoute?.RouteCode}/${stop.StopCode}`
  }

  return (
    <div className={`stops-view ${showMapView ? 'map-active' : ''}`}>
      {selectedLine && (
        <RouteHeader
          line={selectedLine}
          routes={routes}
          selectedRoute={selectedRoute}
          onRouteSelect={onRouteSelect}
          lang={lang}
        />
      )}

      {selectedRoute && <LiveBuses routeCode={selectedRoute.RouteCode} lang={lang} />}

      <div className="view-toggle">
        <button
          className={`toggle-btn ${!showMapView ? 'active' : ''}`}
          onClick={() => setShowMapView(false)}
        >
          {t.showList}
        </button>
        <button
          className={`toggle-btn ${showMapView ? 'active' : ''}`}
          onClick={() => setShowMapView(true)}
        >
          🗺️ {t.showMap}
        </button>
      </div>

      {showMapView || !isMobile ? (
        <div className="map-and-list-container">
          <div className="map-wrapper">
            <RouteMap
              stops={stops}
              selectedStop={selectedStop}
              onStopSelect={onStopSelect}
              lineId={selectedLine?.LineID || ''}
              lang={lang}
              searchQuery={mapStopSearch}
              onSearchChange={setMapStopSearch}
              routeDirection={selectedRoute ? getRouteDirection(selectedRoute, lang) : undefined}
              userLocation={userLocation}
            />
          </div>
          {!isMobile && (
            <div className="stops-list desktop-list">
              {stops.map((stop, idx) => {
                const isStart = idx === 0
                const isEnd = idx === stops.length - 1
                return (
                  <Link
                    key={stop.StopCode}
                    to={getStopHref(stop)}
                    className={`stop-card ${selectedStop?.StopCode === stop.StopCode ? 'selected' : ''} ${isStart ? 'start-stop' : ''} ${isEnd ? 'end-stop' : ''}`}
                  >
                    <div className={`stop-order ${isStart ? 'start' : ''} ${isEnd ? 'end' : ''}`}>{stop.RouteStopOrder}</div>
                    <div className="stop-name">{getStopDescription(stop)}</div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="stops-list">
          {stops.map((stop, idx) => {
            const isStart = idx === 0
            const isEnd = idx === stops.length - 1
            return (
              <Link
                key={stop.StopCode}
                to={getStopHref(stop)}
                className={`stop-card ${isStart ? 'start-stop' : ''} ${isEnd ? 'end-stop' : ''}`}
              >
                <div className={`stop-order ${isStart ? 'start' : ''} ${isEnd ? 'end' : ''}`}>{stop.RouteStopOrder}</div>
                <div className="stop-name">{getStopDescription(stop)}</div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
